<template>
    <div class="name-number-table" v-if="showTable">
        <div class="table-header">
            <span class="header-title">Enter Name/Number</span>
            <div class="header-right">
                <button class="clear-all-btn" @click.stop="onClearAll">
                    Clear All
                </button>
                <button class="collapse-btn" @click="isCollapsed = !isCollapsed">
                <svg
                    v-if="isCollapsed"
                    class="collapse-icon"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M6 9L12 15L18 9"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
                <svg
                    v-else
                    class="collapse-icon"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M6 15L12 9L18 15"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
                </button>
            </div>
        </div>

        <p class="info-text">Full team list required for accurate pricing.</p>

        <div class="table-container" v-show="!isCollapsed">
            <div class="table-row table-row-header">
                <div class="table-cell cell-id">ID</div>
                <div class="table-cell cell-name" v-if="addName">Name</div>
                <div class="table-cell cell-number" v-if="addNumber">
                    Number
                </div>
                <div class="table-cell cell-size">Size</div>
                <div class="table-cell cell-action"></div>
            </div>

            <div class="table-body">
                <div
                    class="table-row"
                    v-for="(entry, index) in entries"
                    :key="entry.id"
                >
                    <div class="table-cell cell-id">{{ entry.id }}</div>
                    <div class="table-cell cell-name" v-if="addName">
                        <input
                            type="text"
                            class="table-input"
                            placeholder="Enter Name"
                            :value="entry.name"
                            @input="
                                onEntryChange(
                                    index,
                                    'name',
                                    $event.target.value
                                )
                            "
                            @blur="onEntryBlur"
                            maxlength="15"
                            :style="{ textTransform: 'uppercase' }"
                        />
                    </div>
                    <div class="table-cell cell-number" v-if="addNumber">
                        <input
                            type="text"
                            class="table-input table-input-number"
                            placeholder="#"
                            :value="entry.number"
                            @input="onNumberInput(index, $event)"
                            @blur="onEntryBlur"
                            maxlength="2"
                        />
                    </div>
                    <div class="table-cell cell-size">
                        <span class="size-value">{{ entry.size }}</span>
                    </div>
                    <div class="table-cell cell-action">
                        <button
                            class="visibility-btn"
                            @click="toggleVisibility(index)"
                            title="Show"
                            :disabled="!entryHasNameOrNumber(entry)"
                        >
                            <svg
                                class="eye-icon"
                                :class="{ 'eye-icon-disabled': !entryHasNameOrNumber(entry) }"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M2 12C4.5 7 9 4 12 4C15 4 19.5 7 22 12C19.5 17 15 20 12 20C9 20 4.5 17 2 12Z"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    fill="none"
                                />
                                <circle
                                    cx="12"
                                    cy="12"
                                    r="3"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    fill="none"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "NameNumberTable",
    props: {
        entries: {
            type: Array,
            default: function () {
                return [];
            }
        },
        addName: { type: Boolean, default: false },
        addNumber: { type: Boolean, default: false },
        saveButtonText: { type: String, default: "SAVE" }
    },
    data() {
        return { isCollapsed: false };
    },
    computed: {
        showTable() {
            return this.addName || this.addNumber;
        }
    },
    methods: {
        onEntryChange(index, field, value) {
            var updated = (this.entries || []).slice();
            var row = updated[index];
            if (!row) return;
            updated[index] = Object.assign({}, row);
            // Name: letters/spaces only, no numbers
            if (field === "name") {
                value = (value || "").replace(/\d/g, "").toUpperCase();
            } else {
                value = value || "";
            }
            updated[index][field] = value;
            this.$emit("update:entries", updated);
        },
        onNumberInput(index, event) {
            var val = (event.target.value || "").replace(/\D/g, "");
            if (val.length > 2) val = val.slice(0, 2);
            event.target.value = val;
            var updated = (this.entries || []).slice();
            var row = updated[index];
            if (!row) return;
            updated[index] = Object.assign({}, row, { number: val, numberUserEdited: true });
            this.$emit("update:entries", updated);
        },
        onEntryBlur() {
            this.$emit("entryBlur");
        },
        entryHasNameOrNumber(entry) {
            if (!entry) return false;
            var hasName = this.addName && entry.name != null && String(entry.name).trim() !== "";
            var hasNumber = this.addNumber && entry.number != null && String(entry.number).trim() !== "";
            return hasName || hasNumber;
        },
        toggleVisibility(index) {
            var updated = (this.entries || []).slice();
            var row = updated[index];
            if (!row) return;
            updated[index] = Object.assign({}, row, { visible: !row.visible });
            this.$emit("update:entries", updated);
            this.$emit("previewRow", index);
        },
        onClearAll() {
            this.$emit("clearAll");
        }
    }
};
</script>

<style scoped>
.name-number-table {
    background: #fff;
    border-top: 1px solid #e5e5e5;
    padding: 12px;
    margin-top: 8px;
    box-shadow: 0px 2px 8px 0px #00000014;
}
.table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 12px;
    margin-bottom: 8px;
    border-bottom: 1px solid #e5e5e5;
}
.header-title {
    font-weight: 600;
    font-size: 0.95rem;
    color: #333;
}
.header-right {
    display: flex;
    align-items: center;
    gap: 6px;
}
.clear-all-btn {
    background: none;
    border: none !important;
    padding: 4px 6px;
    cursor: pointer;
    font-size: 0.85rem;
    color: #1D6CB5;
    font-size: 14px;
    line-height: 14px;
    font-weight: 500;
}
.collapse-btn {
    background: none;
    border: none !important;
    padding: 4px;
    cursor: pointer;
    color: #333;
}
.collapse-icon {
    width: 20px;
    height: 20px;
}
.info-text {
    font-size: 0.85rem;
    color: #666;
    margin-bottom: 0px;
}
.table-container {
    overflow-x: hidden;
    margin-bottom: 16px;
    margin-top: 16px;
    width: 100%;
}
.table-row {
    display: flex;
    align-items: center;
    border-bottom: 1px solid #eee;
    min-width: 0;
}
.table-row-header {
    font-weight: 600;
    background: #f5f5f5;
}
.table-row-header .table-cell{
    color: #121212;
    font-weight: 400;
    font-size: 16px;
    line-height: 16px;
}
.table-body .table-cell{
     color: #333;
    font-weight: 400;
    font-size: 16px;
    line-height: 16px;
}
.table-cell {
    padding: 8px;
    min-width: 0;
}
.cell-id {
    flex: 0 0 36px;
    width: 36px;
}
.cell-name {
    flex: 1 1 auto;
    min-width: 60px;
}
.cell-number {
    flex: 0 0 64px;
    width: 64px;
    text-align: center;
}
.cell-size {
    flex: 0 0 44px;
    width: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.size-value {
    font-size: 0.9rem;
    font-weight: 600;
    color: #333;
}
.cell-action {
    flex: 0 0 36px;
    width: 36px;
}
.table-input {
    width: 100%;
    padding: 6px 4px;
    border: none;
    border-radius: 0;
    font-size: 0.9rem;
    box-sizing: border-box;
    background: transparent;
    outline: none;
}
.table-input::placeholder {
    color: #999;
}
.table-input-number {
    width: 100%;
    text-align: center;
    box-sizing: border-box;
}
.table-input-number::placeholder {
    color: #999;
}

/* Prevent header labels "Number" and "Size" from visually running together */
.table-row-header .cell-number,
.table-row-header .cell-size {
    white-space: nowrap;
}
.visibility-btn {
    background: none;
    border: none !important;
    padding: 4px;
    cursor: pointer;
}
.visibility-btn:disabled {
    cursor: not-allowed;
    opacity: 0.5;
}
.eye-icon {
    width: 18px;
    height: 18px;
}
.eye-icon-disabled {
    opacity: 0.4;
}
.table-body {
    max-height: 280px;
    overflow-y: auto;
}
</style>
